import { useEffect, useRef, useCallback } from 'react';
import useNotificationStore from '@/store/NotificationStore';
import { useAuth } from '@/store/authStore';
import { useNotificationSound } from './useNotificationSound';

interface UseNotificationPollingOptions {
  interval?: number; // Default: 60000 (1 minute)
  enabled?: boolean;
}

export const useNotificationPolling = (options: UseNotificationPollingOptions = {}) => {
  const { interval = 60000, enabled = true } = options;
  const { fetchUnreadCount, hasNewNotification, clearNewNotificationFlag } = useNotificationStore();
  const { isAuthenticated } = useAuth();
  const { playSound } = useNotificationSound();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef<boolean>(true);

  // Play sound when new notification arrives
  useEffect(() => {
    if (hasNewNotification) {
      playSound();
      clearNewNotificationFlag();
    }
  }, [hasNewNotification, playSound, clearNewNotificationFlag]);

  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Initial fetch
    fetchUnreadCount();

    // Set up polling
    intervalRef.current = setInterval(() => {
      if (isVisibleRef.current) {
        fetchUnreadCount();
      }
    }, interval);
  }, [fetchUnreadCount, interval]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Handle visibility change to pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';

      // Fetch immediately when tab becomes visible
      if (isVisibleRef.current && isAuthenticated && enabled) {
        fetchUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount, isAuthenticated, enabled]);

  // Main polling effect
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      stopPolling();
      return;
    }

    startPolling();

    return () => {
      stopPolling();
    };
  }, [enabled, isAuthenticated, startPolling, stopPolling]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return { refresh };
};

export default useNotificationPolling;
